import React, { useState, useRef, useEffect } from 'react';
import Select, { components } from 'react-select'
import CreatableSelect from 'react-select/creatable';
import { AsyncPaginate, wrapMenuList, withAsyncPaginate } from "react-select-async-paginate";
import { isMobile } from "react-device-detect";
import { useTranslation } from 'react-i18next';

const formatOptionLabel = ({ ...data }, { context }, props) => {

    var cols = [];
    if (props.asyncPaginate) {
        cols = props.columns;
    }
    else {
        cols = props.options ? props.options.columns : [];
    }

    const tifOptions = cols.map((col) =>
        <div key={col.field} className={col.flex ? "selectTableOptionCell col-" + col.flex : "selectTableOptionCell col"}>
            {col.asLabel ? data["label"] : data[col.field]}
        </div>
    )
    if (context === "value") {
        return <div>{data["label"]}</div>;
    } else if (context === "menu") {
        return (
            <div className="selectTableOptionRow">
                {tifOptions}
            </div>
        );
    }
};

const getOptionLabel = ({ option }) => {

    var keys = Object.keys(option);
    var str = "";
    keys.forEach((k) => {
        if (k != "value") {
            str += option[k] + " ";
        }

    });
    return str;
};

const { MenuList } = components;

const CustomMenuList = ({ selectProps, ...props }) => {

    const headerCells = selectProps.columns.map(col => (
        <div key={col.field} className={col.flex ? "selectTableHeaderCell col-" + col.flex : "selectTableHeaderCell col"}>
            {col.header}
        </div>
    ));

    const btns = selectProps.footer
        ? selectProps.footer.buttons.map((btn, index) => (
            <button
                key={"footerbtn_" + index}
                onClick={(e) => { e.preventDefault(); btn.onClick(); }}
                className={btn.className + " react-select-shortcut"}
            >
                <i className={btn.icon}></i>
            </button>
        ))
        : [];

    const hideHeader = selectProps.columns.every(col => col.hideHeader);

    return (
        <div className="selectTableDropDown">
            <div className="selectTableHeaderRow" style={hideHeader ? { height: 0, overflow: 'hidden' } : undefined}>
                {headerCells}
            </div>
            <MenuList className="selectTableMenuList" {...props} selectProps={selectProps} />
            {selectProps.footer && (
                <div className="selectTableFooterRow">
                    <div className="btn-group">
                        {btns}
                    </div>
                </div>
            )}
        </div>
    );
};

const defaultAdditional = {
    page: 1
};

const getWidth = (width) => {
    return isMobile ? (width == undefined ? '100%' : width) : "100%";

}

const CreatableAsyncPaginate = withAsyncPaginate(CreatableSelect);


const SelectTable = (props) => {
    const selectRef = useRef();
    const { t } = useTranslation();

    const [selectedOption, setSelectedOption] = useState(props.defaultValue);

    const setValue = (value) => {
        // Maintaining the same state structure as original code (not really used elsewhere though)
        setSelectedOption(value);
    };

    const handleChange = (value) => {
        if (value == null) {
            setValue('');
            props.onChangeValue('');
        } else {
            setValue(value.label);
            props.onChangeValue(value);
        }
    };

    const hangleInputChange = (value, input) => {
        props.onInputChange(value, input);
    };

    const hangleOnBlur = (value) => {
        props.onBlur(value);
    };

    const hangleAsyncInputChange = (value) => {
        setValue(value);
    };

    const commonProps = {
        placeholder: props.placeholder,
        inputId: props.inputId,
        openOnFocus: true,
        menuIsOpen: props.menuIsOpen,
        isOpen: true,
        className: props.className,
        classNamePrefix: props.classNamePrefix,
        maxMenuHeight: 200,
        menuPortalTarget: props.menuPortalTarget,
        menuPosition: props.menuPortalTarget ? 'fixed' : undefined,
        components: {
            MenuList: props.asyncPaginate ? wrapMenuList(CustomMenuList) : CustomMenuList,
        },
        styles: {
            menuPortal: (provided) => ({
                ...provided,
                zIndex: 9999,
            }),
            menu: (provided) => ({
                ...provided,
                width: getWidth(props.width),
                minWidth: props.menuWidth,
                zIndex: 9999,
                borderRadius: '10px',
                boxShadow: '0 8px 28px rgba(1, 50, 137, 0.15)',
                overflow: 'hidden',
                border: '1px solid rgba(1, 50, 137, 0.12)',
                padding: 0,
            }),
            menuList: (provided) => ({
                ...provided,
                padding: 0,
            }),
            indicatorsContainer: (provided) => ({
                ...provided,
                padding: '0px',
                height: '38px',
            }),
            valueContainer: (provided) => ({
                ...provided,
                padding: '0 8px',
            }),
            placeholder: (provided) => ({
                ...provided,
                fontSize: '12px',
            }),
            control: (provided, state) => ({
                ...provided,
                fontSize: '14px',
                minHeight: '38px',
                height: '38px',
                ...(state.isDisabled ? {
                    backgroundColor: '#f4f6fc',
                    borderColor: '#dde3f5',
                    cursor: 'not-allowed',
                } : {}),
            }),
            singleValue: (provided, state) => ({
                ...provided,
                color: state.isDisabled ? 'rgba(1, 50, 137, 0.45)' : provided.color,
            }),
            option: (provided, state) => ({
                ...provided,
                padding: '0',
                backgroundColor: (state.isSelected && !props.isMulti)
                    ? state.isFocused ? 'rgba(1,50,137,0.14)' : 'rgba(1,50,137,0.09)'
                    : state.isFocused ? '#eef2ff' : 'white',
                color: (state.isSelected && !props.isMulti) ? 'var(--appcolor)' : '#1a1a2e',
                fontWeight: (state.isSelected && !props.isMulti) ? 600 : 400,
                cursor: 'pointer',
            }),
        },
    };

    if (props.asyncPaginate) {
        const asyncCommonProps = {
            value: props.selectedValue || props.value,
            loadOptions: (q, prevOptions, { page }) =>
                props.loadOptions(q, prevOptions, { page }, props),
            additional: defaultAdditional,
            getOptionLabel: (option) => getOptionLabel({ option }),
            formatOptionLabel: (data, meta) => formatOptionLabel({ ...data }, meta, props),
            loadOptionsOnMenuOpen: true,
            columns: props.columns || [],
            ...commonProps,
        };

        if (props.createTable) {
            return (
                <CreatableAsyncPaginate
                    {...asyncCommonProps}
                    onChange={handleChange}
                    onInputChange={hangleInputChange}
                    inputValue={props.inputValue}
                    isMulti={props.isMulti}
                    isClearable
                    isDisabled={props.isDisabled}
                    formatCreateLabel={val => `${t("Create")} "${val}"`}
                />
            );
        } else {
            return (
                <AsyncPaginate
                    {...asyncCommonProps}
                    onChange={handleChange}
                    onInputChange={hangleAsyncInputChange}
                    defaultValue={props.defaultValue}
                    isMulti={props.isMulti}
                    isClearable={props.isClearable}
                    isDisabled={props.isDisabled}
                />
            );
        }
    } else {
        const baseSelectProps = {
            onChange: handleChange,
            getOptionLabel: (option) => getOptionLabel({ option }),
            formatOptionLabel: (data, meta) => formatOptionLabel({ ...data }, meta, props),
            ...commonProps,
        };

        if (props.createTable) {
            return (
                <CreatableSelect
                    {...baseSelectProps}
                    onInputChange={hangleInputChange}
                    inputValue={props.inputValue}
                    defaultValue={props.defaultValue}
                    isClearable
                    isMulti={props.isMulti}
                    options={props.options?.data || []}
                    columns={props.options?.columns || []}
                    ref={selectRef}
                    isDisabled={props.isDisabled}
                    formatCreateLabel={val => `${t("Create")} "${val}"`}
                />
            );
        } else {
            return (
                <Select
                    {...baseSelectProps}
                    defaultValue={props.defaultValue}
                    value={props.selectedValue}
                    options={props.options?.data || []}
                    columns={props.options?.columns || []}
                    isDisabled={props.isDisabled}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            const menuIsOpen = window.selectRef.current?.state?.menuIsOpen;
                            if (!menuIsOpen) e.preventDefault();
                        }
                    }}
                    ref={selectRef}
                />
            );
        }
    }
};

export default SelectTable;